/**
 * PopUp class
 * Makes pop-ups interactive based on a given pop-up ID
 */
class PopUp {
  /**
   * PopUp constructor
   * @param popUpId
   */
  constructor(popUpId) {
    //validate pop-up ID parameter
    if (!popUpId)
      throw new Error(
        "Missing required parameter `popUpId` in PopUp constructor"
      );

    //get target element
    const $targetElement = document.querySelector(
      `[data-pop-up-target-id="${popUpId}"]`
    );

    const $triggerElements = Array.from(
      document.querySelectorAll(`[data-pop-up-trigger-id="${popUpId}"]`)
    );

    //validate target element (we don't require trigger elements)
    if (!$targetElement) {
      throw new Error("Target element not found in PopUp constructor.");
    }

    this.state = {
      popUpId,
      isOpen: false,
      $targetElement,
      $backgroundElement: $targetElement.querySelector(
        ".component-global-popup__background"
      ),
      $closeElement: $targetElement.querySelector(
        ".component-global-popup__close"
      ),
      $triggerElements,
      openEvent: new CustomEvent("component-global-popup:open", {
        detail: { popUpId },
      }),
      closeEvent: new CustomEvent("component-global-popup:close", {
        detail: { popUpId },
      }),
    };

    //bind click events on the trigger elements (only if there are any)
    if (this.state.$triggerElements.length > 0) {
      this.state.$triggerElements.forEach(($triggerElement) => {
        const triggerType = $triggerElement.dataset.popUpTriggerType;
        if (!triggerType || triggerType !== "submit") {
          $triggerElement.addEventListener(
            "click",
            this.handleTriggerClick.bind(this)
          );
        }
      });
    }

    //close pop-up on background click
    if (this.state.$backgroundElement) {
      this.state.$backgroundElement.addEventListener(
        "click",
        this.handleBackgroundClick.bind(this)
      );
    }

    //close pop-up on close click
    if (this.state.$closeElement) {
      this.state.$closeElement.addEventListener(
        "click",
        this.handleCloseClick.bind(this)
      );
    }

    //close pop-up on escape key press
    document.addEventListener("keydown", this.handleKeyDown.bind(this));

    //close this pop-up when other pop-up opens
    window.addEventListener(
      "component-global-popup:open",
      this.handleOtherPopUpOpen.bind(this)
    );

    // Listen for "trigger-open" and "trigger-close" events for this specific pop-up
    window.addEventListener(
      `component-global-popup:trigger-open-${popUpId}`,
      this.handleTriggerOpen.bind(this)
    );
    window.addEventListener(
      `component-global-popup:trigger-close-${popUpId}`,
      this.handleTriggerClose.bind(this)
    );
  }

  /**
   * Handle trigger click event
   * @param event
   */
  handleTriggerClick(event) {
    if (event) event.preventDefault();
    this.state.isOpen ? this.deactivate() : this.activate();
  }

  /**
   * Handle background click event
   * @param event
   */
  handleBackgroundClick(event) {
    if (event) event.preventDefault();
    if (this.state.isOpen) this.deactivate();
  }

  /**
   * Handle close click event
   * @param event
   */
  handleCloseClick(event) {
    if (event) event.preventDefault();
    if (this.state.isOpen) this.deactivate();
  }

  /**
   * Handle key down event
   * @param event
   */
  handleKeyDown(event) {
    if (event.key.toLowerCase() === "escape" && this.state.isOpen)
      this.deactivate();
  }

  /**
   * Handle other pop-up open event
   * @param event
   */
  handleOtherPopUpOpen(event) {
    const popUpId = event?.detail?.popUpId;
    if (popUpId && popUpId !== this.state.popUpId) {
      if (this.state.isOpen) this.deactivate();
    }
  }

  /**
   * Activate pop-up
   */
  activate() {
    if (!this.state.isOpen) {
      this.state.$targetElement.classList.add("component-global-popup--active");
      window.dispatchEvent(this.state.openEvent);
      this.state.isOpen = true;

      // Get the trustbadge from the page if present.
      const $trustBadge = document.querySelector(
        '[id^="trustbadge-container-"]'
      );

      // Hide the trustbadge when popups are active.
      if ($trustBadge) $trustBadge.style.display = "none";
    }
  }

  /**
   * Deactivate pop-up
   */
  deactivate() {
    if (this.state.isOpen) {
      this.state.$targetElement.classList.remove(
        "component-global-popup--active"
      );
      window.dispatchEvent(this.state.closeEvent);
      this.state.isOpen = false;

      // Get the trustbadge from the page if present.
      const $trustBadge = document.querySelector(
        '[id^="trustbadge-container-"]'
      );

      // Hide the trustbadge when popups are active.
      if ($trustBadge) $trustBadge.style.display = "block";
    }
  }

  /**
   * Handle an external "trigger-open" event
   * @param event
   */
  handleTriggerOpen(event) {
    this.activate();
  }

  /**
   * Handle an external "trigger-close" event
   * @param event
   */
  handleTriggerClose(event) {
    this.deactivate();
  }
}

export default PopUp;
